import React, { useEffect, useMemo, useState } from 'react';
import { WavesIcon } from 'lucide-react';
import type { QuestionRendererProps } from './types';
import { MapCanvas, type LinePath, type CityMarker } from '../../../visuals/components/topoMap/MapCanvas';
import { FALLBACK_COLOR } from './rendererUtils';
import riversData from '../../../content/atlases/nl/rivers.json';
import citiesData from '../../../content/atlases/nl/cities.json';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
/**
 * MapLocateLineRenderer — Click on the correct river/waterway.
 *
 * UX (tap-and-snap, mei 2026):
 *   The kid sees the NL map with all hoofdrivieren as soft blue lines.
 *   A tap anywhere on the map snaps to the NEAREST river — the chosen
 *   river lights up in module-color with a soft glow, while the others
 *   fade to muted blue. The submit payload is the SNAPPED point on the
 *   chosen river (still {lat, lon}), so the existing point-to-polyline
 *   validator keeps working unchanged: picking the wrong river yields a
 *   large distance to targetRiverId; picking the right one yields ≈0.
 *
 * Data shape:
 *   data.mapId: string (e.g. "nl-provinces")
 *   data.targetRiverId: string (must match an id in atlases/nl/rivers.json)
 *   data.toleranceKm: number (typically 8-15)
 *   data.referenceCityIds?: string[] (optional reference markers for orientation)
 *
 * Answer payload: JSON.stringify({ lat, lon })
 */
export function MapLocateLineRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  // R-13 fix: all hooks MUST run unconditionally, BEFORE any early returns.
  // The previous version had `if (!question.data) return null` before the
  // useState calls, which violates the Rules of Hooks whenever question.data
  // is briefly missing during a question swap.
  const color = moduleColor || FALLBACK_COLOR;
  const data = (question.data ?? {}) as Record<string, unknown>;
  const mapId = String(data.mapId ?? '');
  const [pickedRiverId, setPickedRiverId] = useState<string | null>(null);
  const [pickedPoint, setPickedPoint] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  useEffect(() => {
    setPickedRiverId(null);
    setPickedPoint(null);
  }, [question.id]);
  const isReady = pickedPoint !== null;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  const answerPayload = useMemo(() => pickedPoint ? JSON.stringify(pickedPoint) : '', [pickedPoint]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const linePaths: LinePath[] = useMemo(() => {
    const rivers = (riversData?.rivers ?? []) as Array<{
      id: string;
      name: string;
      path: [number, number][];
    }>;
    return rivers.map(r => ({
      id: r.id,
      points: r.path
    }));
  }, []);
  const referenceIds = Array.isArray(data.referenceCityIds) ? data.referenceCityIds as string[] : [];
  const referenceCityMarkers: CityMarker[] = useMemo(() => {
    if (referenceIds.length === 0) return [];
    const all = (citiesData?.cities ?? []) as Array<{
      id: string;
      name: string;
      lat: number;
      lon: number;
    }>;
    return all.filter(c => referenceIds.includes(c.id)).map(c => ({
      id: c.id,
      name: c.name,
      lat: c.lat,
      lon: c.lon
    }));
  }, [referenceIds]);
  // Guard AFTER all hooks have run. Safe to early-return now.
  if (!question.data || !mapId) return null;
  const handleLineSelect = (lineId: string, snapped: {
    lat: number;
    lon: number;
  }) => {
    const isNewRiver = lineId !== pickedRiverId;
    setPickedRiverId(lineId);
    setPickedPoint(snapped);
    if (isNewRiver) hapticManager.hapticSelection();
  };
  return <div className="flex flex-col items-center gap-3 w-full" data-id="element-11578">
      <div className="text-sm text-stone-500 flex items-center gap-1.5" data-id="element-11579">
        <WavesIcon className="w-4 h-4" data-id="element-11580" />
        Tik op de juiste rivier
      </div>

      <MapCanvas mapId={mapId} mode="select-point" onLineSelect={handleLineSelect} selectedLineId={pickedRiverId} selectedPoint={null} linePaths={linePaths} cityMarkers={referenceCityMarkers} width={420} height={420} ariaLabel={question.question} moduleColor={color} data-id="element-11581" />

      <p className="text-sm text-stone-500 flex items-center gap-1.5 min-h-[1.25rem]" aria-live="polite" data-id="element-11582">
        {pickedRiverId ? 'Rivier gekozen — tik op een andere om te veranderen' : '\u00A0'}
      </p>
    </div>;
}